import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Briefcase, ChevronRight, Menu, X } from "lucide-react";
import Sidebar, { JOURNEY_NAV, pathMatches } from "./Sidebar";
import OnboardingTour from "./OnboardingTour";
import FeedbackWidget from "./FeedbackWidget";
import BrandLogo from "@/components/BrandLogo";
import { useWorkspace } from "@/lib/workspace-context";
import { useAuth } from "@/lib/auth-context";

/**
 * Persistent context strip - shown at the top of every /app/* page ONLY when
 * the user is prepping for 2+ interviews. It reinforces which interview the
 * scores/lists on screen belong to, so the workspace concept never has to be
 * explained. For single-interview users, the strip is hidden (implicit).
 */
function InterviewContextStrip() {
  const { workspaces, active } = useWorkspace();
  if (workspaces.length <= 1 || !active) return null;
  return (
    <div
      data-testid="interview-context-strip"
      className="sticky top-0 z-20 border-b border-zinc-800/60 bg-[#0A0A0B]/90 backdrop-blur-md"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-2.5 flex items-center gap-2 md:gap-3">
        <span className="h-6 w-6 rounded-md bg-amber-500/10 border border-amber-500/25 flex items-center justify-center flex-shrink-0">
          <Briefcase className="h-3 w-3 text-amber-400" />
        </span>
        <div className="hidden sm:block text-[10px] uppercase tracking-[0.24em] text-zinc-500">Preparing for</div>
        <div
          className="text-sm text-zinc-100 font-medium truncate min-w-0"
          data-testid="interview-context-strip-name"
        >
          {active.name}
        </div>
        <div className="flex-1" />
        <Link
          to="/app/workspaces"
          data-testid="interview-context-strip-manage"
          className="hidden sm:inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-200 transition-colors whitespace-nowrap"
        >
          Manage interviews <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { activeId } = useWorkspace();
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever the route changes so navigation feels
  // native (tapping a nav link on phone shouldn't leave the drawer open).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when the mobile drawer is open.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen relative">
      <div className="ambient-bg" />

      {/* Mobile top bar, visible only below md. Contains hamburger + brand */}
      <header
        data-testid="app-mobile-header"
        className="md:hidden sticky top-0 z-40 flex items-center gap-3 px-4 h-14 border-b border-zinc-800/60 bg-[#0A0A0B]/95 backdrop-blur-md"
      >
        <button
          data-testid="app-mobile-menu-btn"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
          className="h-9 w-9 -ml-1 flex items-center justify-center rounded-md text-zinc-300 hover:text-zinc-50 hover:bg-zinc-900 transition-colors"
        >
          <Menu className="h-5 w-5" />
        </button>
        <BrandLogo variant="lockup" iconSize={26} to="/app/dashboard" testid="mobile-header-brand" />
        {(() => {
          const stage = JOURNEY_NAV.find((j) => pathMatches(location.pathname, j.to, j.extraPaths));
          if (!stage) return null;
          return (
            <button
              data-testid="mobile-journey-chip"
              aria-label={`Journey stage ${stage.num} of 05 — ${stage.title}. Open navigation`}
              onClick={() => setMobileOpen(true)}
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/[0.08] px-2.5 py-1 text-[10px] font-semibold text-emerald-300 whitespace-nowrap max-w-[52%]"
            >
              <span className="tabular-nums flex-shrink-0">{stage.num} of 05</span>
              <span className="text-emerald-200/90 truncate">· {stage.title}</span>
            </button>
          );
        })()}
      </header>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="app-mobile-backdrop"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          aria-hidden
        />
      )}

      <Sidebar mobileOpen={mobileOpen} onCloseMobile={() => setMobileOpen(false)} />

      <main className="md:ml-64 min-h-screen">
        {user?.is_guest && (
          <div data-testid="guest-mode-banner" className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 px-4 py-2 text-[12px] bg-amber-500/10 border-b border-amber-500/20 text-amber-200">
            <span>You're exploring Intuiterview as a guest (samples only).</span>
            <Link to="/register" data-testid="guest-banner-signup" className="font-semibold underline underline-offset-2 hover:text-amber-100">
              Create your free account
            </Link>
          </div>
        )}
        <InterviewContextStrip />
        {/* Keying on activeId forces every child page to remount whenever the
            user switches interviews, so lists, dashboards, and setup panels
            all refetch scoped data automatically. */}
        <div key={activeId || "no-workspace"} className="max-w-[1400px] mx-auto px-4 py-6 md:px-10 md:py-10">
          {children}
        </div>
      </main>
      {!user?.is_guest && <OnboardingTour />}
      <FeedbackWidget />
    </div>
  );
}
